/**
 * Refill verification — compare a freshly-scanned barcode against the
 * value saved on the prescription document.
 *
 * Pure functions only — no React, no API calls. Designed so the verdict
 * logic is easy to unit-test (and trivially auditable).
 *
 * Verdicts:
 *   • "no-saved-barcode" — the prescription record has no barcode on file
 *                          → we can't verify, but offer to ADD the scan as
 *                          the canonical barcode for next time.
 *   • "no-scan"          — no scanned barcode was provided.
 *   • "match"            — saved and scanned values are equivalent.
 *   • "mismatch"         — values differ → almost always a labelling
 *                          or dispensing problem; surface the red banner.
 */

/**
 * Normalise a barcode payload for comparison.
 *
 *   • Trim whitespace
 *   • Strip ZXing's GS1 group-separator (ASCII 29) which sometimes leaks
 *     into the decoded text and would cause spurious mismatches.
 *   • Strip leading "]C1" AIM-identifier prefixes that Data Matrix readers
 *     emit for GS1 application identifiers.
 *
 * We DO NOT change case — barcodes are case-sensitive by spec, and most
 * pharmacy formats are numeric anyway.
 */
export function normalizeBarcode(raw) {
  if (raw == null) return "";
  let s = String(raw).trim();
  // ZXing GS1 group separator
  s = s.replace(/\u001d/g, "");
  // AIM identifier prefix
  s = s.replace(/^\]C1/, "").replace(/^\]d2/, "");
  return s;
}

/**
 * @param {string} saved    Barcode stored on the prescription.
 * @param {string} scanned  Barcode just read from the bottle.
 * @returns {{ verdict: string, savedNorm: string, scannedNorm: string }}
 */
export function compareBarcodes(saved, scanned) {
  const savedNorm = normalizeBarcode(saved);
  const scannedNorm = normalizeBarcode(scanned);
  if (!scannedNorm) return { verdict: "no-scan", savedNorm, scannedNorm };
  if (!savedNorm) return { verdict: "no-saved-barcode", savedNorm, scannedNorm };
  return {
    verdict: savedNorm === scannedNorm ? "match" : "mismatch",
    savedNorm,
    scannedNorm,
  };
}

/**
 * Build the patch payload to send to PATCH /api/prescriptions/{id} for a
 * successful refill verification. Bumps lastFilled to today (local date)
 * and decrements refillsRemaining (floored at 0). Also stamps a verifiedAt
 * timestamp + verdict so the UI can render a provenance chip.
 *
 * Caller is responsible for `import { toLocalDateInputValue } from "./dateUtils"`.
 */
export function buildRefillConfirmPatch(rx, todayIso) {
  const remaining = Math.max(0, (Number(rx.refillsRemaining) || 0) - 1);
  return {
    lastFilled: todayIso,
    refillsRemaining: remaining,
    lastVerifiedAt: new Date().toISOString(),
    lastVerifyVerdict: "match",
  };
}

/**
 * Patch payload for the "Replace barcode and notify pharmacist" action.
 * Replaces the canonical barcode + format and tags the record so we can
 * surface a "replaced after mismatch" chip on the card afterwards.
 */
export function buildBarcodeReplacePatch({ scannedBarcode, scannedFormat }) {
  return {
    barcodeData: normalizeBarcode(scannedBarcode),
    barcodeFormat: scannedFormat || "",
    lastVerifiedAt: new Date().toISOString(),
    lastVerifyVerdict: "replaced",
  };
}
